import { cacheService } from './cacheService';
import { indexedDbService, STORES } from './indexedDbService';
import { getOnlineStatus } from './offlineService';
import { BusinessDetailsDto, BusinessUser } from '../types/business';
import { apiClient } from './apiClient';
import { apiHealthService } from './apiHealthService';

export interface BusinessUserRole {
    userId: string;
    businessId: string;
    role: string;
    isBusinessAdmin: boolean;
}

export interface AddUserDto {
    email: string;
    firstName: string;
    lastName: string;
    businessRole: {
        roleId: string;
        name: string;
    } | null;
}

export interface UpdateUserDto {
    email: string;
    firstName: string;
    lastName: string;
    businessRole?: {
        roleId: string;
        name: string;
    };
}

export interface AddUserResponse {
    user: BusinessUser;
    initialPassword?: string;
    resetToken?: string;
}

export interface Business {
    id: string;
    name: string;
    description?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    timeZoneId?: string;
    useBusinessHours: boolean;
    businessHoursStart: string | null;  // Format: "00:00:00" - TimeSpan from backend
    businessHoursEnd: string | null;    // Format: "00:00:00" - TimeSpan from backend
    restrictToBusinessHours: boolean;
    backgroundColor?: string;
    primaryColor?: string;
    secondaryColor?: string;
    navigationColor?: string;
    textColor?: string;
    accentColor?: string;
    firstDayOfWeek: number;
    defaultCity?: string;
    defaultCountry?: string;
    defaultAddress?: string;
    defaultLatitude?: number;
    defaultLongitude?: number;
}

export interface BusinessFormData {
    id: string;
    name: string;
    description: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    timeZoneId: string;
    useBusinessHours: boolean;
    businessHoursStart: string | null;  // Format: "00:00:00" - TimeSpan for backend
    businessHoursEnd: string | null;    // Format: "00:00:00" - TimeSpan for backend
    restrictToBusinessHours: boolean;
}

// First declare the promise variable outside the class
let businessListPromise: Promise<Business[]> | null = null;
let businessUsersPromise: { [key: string]: Promise<BusinessUser[]> | undefined } = {};

class BusinessService {
    private readonly baseUrl = `${apiHealthService.getApiUrl()}/api/business`;
    // Cache expiration time: 10 minutes
    private readonly CACHE_EXPIRATION = 10 * 60 * 1000;
    // Cache expiration time for rarely changing data: 30 minutes
    private readonly LONG_CACHE_EXPIRATION = 30 * 60 * 1000;

    async getBusinessDetails(businessId: string): Promise<BusinessDetailsDto> {
        try {
            console.log(`Making business details request for ID: ${businessId}`);
            const response = await apiClient.get(`${this.baseUrl}/${businessId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching business details:', error);
            throw error;
        }
    }

    async updateBusinessDetails(details: BusinessDetailsDto, businessId?: string): Promise<void> {
        try {
            const url = businessId ? `${this.baseUrl}/${businessId}` : this.baseUrl;
            await apiClient.put(url, details);
            
            // Invalidate cache for this business
            cacheService.remove(`business-details:${businessId || 'default'}`);
        } catch (error) {
            console.error('Error updating business details:', error);
            throw error;
        }
    }

    async getBusinessUsers(businessId?: string): Promise<BusinessUser[]> {
        const cacheKey = `business-users-${businessId || 'default'}`;
        
        // Check if there's an existing promise
        const existingPromise = businessUsersPromise[cacheKey];
        if (existingPromise) {
            return existingPromise;
        }

        // Check offline status first
        const isOffline = !getOnlineStatus();
        if (isOffline) {
            console.log('Offline mode: Getting business users from IndexedDB');
            try {
                return await indexedDbService.getBusinessUsers(businessId);
            } catch (error) {
                console.error('Error getting business users from IndexedDB:', error);
                return [];
            }
        }

        try {
            // Create new promise and store it
            const promise = (async () => {
                try {
                    const response = await apiClient.get<BusinessUser[]>(`${this.baseUrl}/users`, {
                        params: businessId ? { businessId } : {}
                    });

                    // Store business users in IndexedDB for offline use
                    try {
                        await indexedDbService.saveBusinessUsers(response.data);
                    } catch (dbError) {
                        console.error('Error saving business users to IndexedDB:', dbError);
                    }

                    return response.data;
                } catch (error) {
                    console.error('Error fetching business users:', error);
                    
                    // If API request fails, try to get from IndexedDB
                    try {
                        const offlineUsers = await indexedDbService.getBusinessUsers(businessId);
                        console.log('Retrieved business users from IndexedDB:', offlineUsers);
                        return offlineUsers;
                    } catch (dbError) {
                        console.error('Error getting business users from IndexedDB:', dbError);
                        throw error; // Re-throw the original error
                    }
                }
            })();

            // Store the promise
            businessUsersPromise[cacheKey] = promise;

            // Wait for the result
            const result = await promise;

            // Clear the promise after deduplication window (2 seconds to handle React StrictMode)
            setTimeout(() => {
                delete businessUsersPromise[cacheKey];
            }, 2000);

            return result;
        } catch (error) {
            // Clear the promise on error
            delete businessUsersPromise[cacheKey];
            throw error;
        }
    }

    async addBusinessUser(user: AddUserDto): Promise<AddUserResponse> {
        try {
            const response = await apiClient.post<AddUserResponse>(`${this.baseUrl}/users`, user);
            
            // Invalidate business users cache
            cacheService.removeByPrefix('business-users:');
            
            return response.data;
        } catch (error) {
            console.error('Error adding business user:', error);
            throw error;
        }
    }

    async updateBusinessUser(userId: string, user: UpdateUserDto): Promise<void> {
        try {
            await apiClient.put(`${this.baseUrl}/users/${userId}`, user);
            
            // Invalidate business users cache
            cacheService.removeByPrefix('business-users:');
        } catch (error) {
            console.error('Error updating business user:', error);
            throw error;
        }
    }

    async deleteBusinessUser(userId: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/users/${userId}`);
            
            // Invalidate business users cache
            cacheService.removeByPrefix('business-users:');
        } catch (error) {
            console.error('Error deleting business user:', error);
            throw error;
        }
    }

    // Clear cached business data
    clearCache(): void {
        cacheService.remove('businesses');
        businessListPromise = null;
        // Also clear IndexedDB business and appointment types data
        indexedDbService.clearStore(STORES.BUSINESSES).catch(console.error);
        indexedDbService.clearStore(STORES.APPOINTMENT_TYPES).catch(console.error);
    }

    async getBusinesses(): Promise<Business[]> {
        // If there's already a request in progress, return that promise
        if (businessListPromise) {
            return businessListPromise;
        }

        const cacheKey = 'businesses';
        const isOffline = !getOnlineStatus();
        
        if (isOffline) {
            console.log('Offline mode: Getting businesses from IndexedDB');
            try {
                return await indexedDbService.getBusinesses();
            } catch (error) {
                console.error('Error getting businesses from IndexedDB:', error);
                return [];
            }
        }
        
        try {
            // Create new promise and store it
            businessListPromise = cacheService.getOrSet(
                cacheKey,
                async () => {
                    try {
                        const response = await apiClient.get<Business[]>(`${this.baseUrl}/my-businesses`);
                        
                        // Store businesses in IndexedDB for offline use
                        try {
                            await indexedDbService.saveBusinesses(response.data);
                        } catch (dbError) {
                            console.error('Error saving businesses to IndexedDB:', dbError);
                        }
                        
                        return response.data;
                    } catch (error) {
                        console.error('Error fetching businesses:', error);
                        
                        // If API request fails, try to get from IndexedDB
                        try {
                            const offlineBusinesses = await indexedDbService.getBusinesses();
                            console.log('Retrieved businesses from IndexedDB:', offlineBusinesses);
                            return offlineBusinesses;
                        } catch (dbError) {
                            console.error('Error getting businesses from IndexedDB:', dbError);
                            throw error; // Re-throw the original error
                        }
                    }
                },
                this.CACHE_EXPIRATION
            );

            const result = await businessListPromise;

            // Clear the promise after deduplication window (2 seconds to handle React StrictMode)
            setTimeout(() => {
                businessListPromise = null;
            }, 2000);

            return result;
        } catch (error) {
            businessListPromise = null;
            throw error;
        }
    }

    async getAllBusinesses(): Promise<Business[]> {
        const cacheKey = 'businesses:all';
        
        return cacheService.getOrSet(
            cacheKey,
            async () => {
                try {
                    const response = await apiClient.get<Business[]>('/business/list');
                    return response.data;
                } catch (error) {
                    console.error('Error fetching all businesses:', error);
                    throw error;
                }
            },
            this.CACHE_EXPIRATION
        );
    }

    async updateBusiness(id: string, business: BusinessFormData): Promise<Business> {
        try {
            const response = await apiClient.put<Business>(`${this.baseUrl}?businessId=${id}`, business);
            
            // Invalidate business caches
            cacheService.remove(`business-details:${id}`);
            cacheService.removeByPrefix('businesses:');
            
            return response.data;
        } catch (error) {
            console.error('Error updating business:', error);
            throw error;
        }
    }

    async createBusiness(business: BusinessFormData): Promise<Business> {
        try {
            const response = await apiClient.post<Business>(this.baseUrl, business);
            
            // Invalidate business list caches
            cacheService.removeByPrefix('businesses:');
            
            return response.data;
        } catch (error) {
            console.error('Error creating business:', error);
            throw error;
        }
    }

    async getTimeZones(): Promise<string[]> {
        const cacheKey = 'timezones';
        
        return cacheService.getOrSet(
            cacheKey,
            async () => {
                try {
                    const response = await apiClient.get<string[]>('/timezones');
                    return response.data;
                } catch (error) {
                    console.error('Error fetching time zones:', error);
                    throw error;
                }
            },
            this.LONG_CACHE_EXPIRATION // Time zones rarely change, so use longer cache
        );
    }

    async getBusiness(businessId: string): Promise<Business> {
        try {
            // Enhanced token debugging
            const token = localStorage.getItem('token');
            
            const response = await apiClient.get<Business>(`${this.baseUrl}/${businessId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch business:', error);
            throw error;
        }
    }
}

export const businessService = new BusinessService(); 